import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DoBoxRuleDecision,
  DoBoxRuleDecisionAction,
  WebSocketEntityType,
} from '@this+that/models';

import { useCustomToast } from '../../../hooks/useCustomToast';
import { useGetDoBoxRules } from '../../../services/doBoxRules.service';
import { useDoBoxRulesStore } from '../../../store';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

export function useDoBoxRuleDecisionSync() {
  const { subscribe } = useWebSocket();
  const { t } = useTranslation();
  const { successMessage, warningMessage } = useCustomToast();
  const { getDoBoxRules } = useGetDoBoxRules();

  const handleDecision = useCallback(
    async (decision: DoBoxRuleDecision) => {
      switch (decision.action) {
        case DoBoxRuleDecisionAction.CREATE:
        case DoBoxRuleDecisionAction.REPLACE:
        case DoBoxRuleDecisionAction.MERGE: {
          const fetched = await getDoBoxRules();
          useDoBoxRulesStore.getState().setRules(fetched);
          const toastKeys: Record<string, string> = {
            [DoBoxRuleDecisionAction.CREATE]:
              'doBox.rules.decisionToast.created',
            [DoBoxRuleDecisionAction.REPLACE]:
              'doBox.rules.decisionToast.replaced',
            [DoBoxRuleDecisionAction.MERGE]: 'doBox.rules.decisionToast.merged',
          };
          successMessage(t(toastKeys[decision.action]));
          break;
        }
        case DoBoxRuleDecisionAction.SKIP:
          warningMessage(
            decision.reason || t('doBox.rules.decisionToast.skipped'),
          );
          break;
        default:
          break;
      }
    },
    [getDoBoxRules, successMessage, warningMessage, t],
  );

  useEffect(() => {
    return subscribe<DoBoxRuleDecision>(
      WebSocketEntityType.DoBoxRule,
      WebSocketEventType.INSERT,
      ({ item }) => {
        if (!item) {
          return;
        }
        handleDecision(item);
      },
    );
  }, [subscribe, handleDecision]);
}
